import { useCallback, useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import { database } from "../../database/firebase";
import { createInitialState } from "../shiftTicTacToe/engine/gameLogic";
import { startingPlayerForRematchSeq } from "./alternateStartStorage";
import { resolvePlayerDisplayName } from "../leaderboard/scoreboardStorage";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function toDurationString(input) {
  if (typeof input === "string" && /^\d{2}:\d{2}:\d{2}$/.test(input.trim())) {
    return input.trim();
  }
  const totalSeconds = Math.max(0, Number(input) || 0);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

function parseDurationToSeconds(input) {
  if (input == null || input === "") return 0;
  if (typeof input === "number" && Number.isFinite(input)) {
    return Math.max(0, Math.floor(input));
  }
  const s = String(input).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) {
    const [h, m, sec] = s.split(":").map((n) => Number(n) || 0);
    return h * 3600 + m * 60 + sec;
  }
  return Math.max(0, Math.floor(Number(s) || 0));
}

function buildPlayerStatsForFirestore(scoreUsers, updateResult) {
  const resultByUserId = {};
  const results = Array.isArray(updateResult?.results) ? updateResult.results : [];
  for (const row of results) {
    const uid = String(row.userId ?? "").trim();
    if (!uid) continue;
    resultByUserId[uid] = {
      points: Number(row.points) || 0,
      time: toDurationString(row.totaltime ?? row.totalTime ?? 0),
    };
  }

  return (Array.isArray(scoreUsers) ? scoreUsers : [])
    .map((u) => {
      const uid = String(u.userId ?? "").trim();
      if (!uid) return null;
      const fromApi = resultByUserId[uid];
      const matchTime = toDurationString(u.totalTime ?? u.totaltime ?? 0);
      return {
        userId: uid,
        name: String(u.name ?? "").trim(),
        email: String(u.email ?? "").trim(),
        role: String(u.role ?? "").trim(),
        points: fromApi ? fromApi.points : Number(u.score) || 0,
        time: fromApi ? fromApi.time : matchTime,
      };
    })
    .filter(Boolean);
}

/** Normalize lobby doc shiftGamePlayerStats for scoreboard UI. */
export function readLobbyPlayerStatsFromDoc(docData) {
  const raw = docData?.shiftGamePlayerStats;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      const userId = String(p?.userId ?? "").trim();
      const name = resolvePlayerDisplayName({
        name: p?.name,
        email: p?.email,
        emp_id: p?.emp_id ?? p?.employeeId,
        employeeId: p?.employeeId ?? p?.emp_id,
      });
      const score = Number(p?.points ?? p?.score) || 0;
      if (!userId && !name) return null;
      return { userId, name, email: String(p?.email ?? "").trim(), score };
    })
    .filter(Boolean)
    .slice(0, 2);
}

export function characterToPlayerRole(character) {
  const value = String(character ?? "")
    .trim()
    .toLowerCase();
  if (value === "c1") return "X";
  if (value === "c2") return "O";
  return null;
}

/** Session or lobby character for HUD (numeric 1–6 or c1–c6). */
export function parseHudCharacter(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().toLowerCase();
  if (/^c[1-6]$/.test(s)) return s;
  const n = Number(raw);
  if (Number.isInteger(n) && n >= 1 && n <= 6) return n;
  return null;
}

export function characterHudLabel(character) {
  const parsed = parseHudCharacter(character);
  if (parsed == null) return null;
  if (typeof parsed === "string") return parsed.toUpperCase();
  return `C${parsed}`;
}

/** Public URL for avatar in public/img (c1.png … c6.png). */
export function characterImageSrc(character) {
  const parsed = parseHudCharacter(character);
  if (parsed == null) return null;
  const file = typeof parsed === "string" ? parsed : `c${parsed}`;
  const base = process.env.PUBLIC_URL || "";
  return `${base}/img/${file}.png`;
}

/** VS AI opponent avatar (public/img/computer.png). */
export function aiOpponentImageSrc() {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/img/computer.png`;
}

/** Name from lobby + session user doc; falls back to email or emp_id. */
export function resolveLobbyPlayerDisplayName(lobbyPlayer, sessionData = {}) {
  const lobby = lobbyPlayer || {};
  const session = sessionData || {};
  return resolvePlayerDisplayName({
    name: session.name ?? lobby.name,
    email: session.email ?? lobby.email,
    emp_id:
      session.emp_id ??
      session.employeeId ??
      lobby.emp_id ??
      lobby.employeeId,
    employeeId:
      session.employeeId ??
      session.emp_id ??
      lobby.employeeId ??
      lobby.emp_id,
    firstName: session.firstName ?? session.first_name ?? lobby.firstName,
    lastName: session.lastName ?? session.last_name ?? lobby.lastName,
  });
}

/** X/O panel names + session characters for lobby PVP HUD. */
export async function fetchLobbyHudIdentities(sessionId, lobbyName) {
  const players = await fetchLobbyLivePlayers(sessionId, lobbyName);
  const result = { x: null, o: null };

  await Promise.all(
    players.map(async (p) => {
      let character = null;
      let sessionData = {};
      try {
        const snap = await getDoc(
          doc(database, `sessions/${sessionId}/users/${p.userId}`)
        );
        sessionData = snap.exists() ? snap.data() || {} : {};
        const sessionChar = parseHudCharacter(sessionData.character);
        character =
          sessionChar ?? parseHudCharacter(p.lobbyCharacter);
      } catch (e) {
        console.warn("fetchLobbyHudIdentities: session user", p.userId, e);
      }
      const entry = {
        name: resolveLobbyPlayerDisplayName(p, sessionData),
        character,
      };
      if (p.role === "X") result.x = entry;
      if (p.role === "O") result.o = entry;
    })
  );

  return result;
}

/** All lobby user docs (any status) for scoreboard avatars / id matching. */
export async function fetchLobbyUsersForCharacters(sessionId, lobbyName) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  if (!sid || !lobby) return [];
  try {
    const snap = await getDocs(lobbyUsersCollectionRef(sid, lobby));
    return snap.docs
      .map((d) => {
        const data = d.data() || {};
        const userId = String(data.userId || d.id || "").trim();
        if (!userId) return null;
        return {
          userId,
          name: String(data.name ?? "").trim(),
          lobbyCharacter: data.character,
          role: characterToPlayerRole(data.character),
        };
      })
      .filter(Boolean);
  } catch (e) {
    console.error("fetchLobbyUsersForCharacters failed:", e);
    return [];
  }
}

/** Attach lobby userIds when scoreboard rows only have names. */
export function mergeScoreboardPlayersWithLobby(players, lobbyUsers) {
  const result = (Array.isArray(players) ? players : []).map((p) => ({
    ...p,
    userId: String(p?.userId ?? p?.userid ?? p?.id ?? "").trim(),
    name: String(p?.name ?? "").trim() || "Player",
    score: Number.isFinite(Number(p?.score)) ? Number(p.score) : 0,
  }));
  const pool = Array.isArray(lobbyUsers) ? [...lobbyUsers] : [];

  for (const p of result) {
    if (!p.userId) continue;
    const idx = pool.findIndex((l) => l.userId === p.userId);
    if (idx >= 0) pool.splice(idx, 1);
  }
  for (const p of result) {
    if (p.userId) continue;
    const nameKey = p.name.toLowerCase();
    const idx = pool.findIndex(
      (l) => String(l.name ?? "").trim().toLowerCase() === nameKey
    );
    if (idx >= 0) {
      p.userId = pool[idx].userId;
      pool.splice(idx, 1);
    }
  }
  for (const p of result) {
    if (!p.userId && pool.length) {
      p.userId = pool.shift().userId;
    }
  }
  return result;
}

/** userId → character image URL (session user doc, lobby slot fallback). */
export async function fetchCharacterImagesByUserIds(
  sessionId,
  lobbyName,
  userIds
) {
  const sid = String(sessionId ?? "").trim();
  const ids = [
    ...new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id ?? "").trim())
        .filter(Boolean)
    ),
  ];
  if (!sid || !ids.length) return {};

  const lobby = String(lobbyName ?? "").trim();
  const lobbyCharByUid = {};
  if (lobby) {
    const lobbyUsers = await fetchLobbyUsersForCharacters(sid, lobby);
    lobbyUsers.forEach((p) => {
      lobbyCharByUid[p.userId] = p.lobbyCharacter;
    });
  }

  const result = {};
  await Promise.all(
    ids.map(async (uid) => {
      let character = null;
      try {
        const snap = await getDoc(
          doc(database, `sessions/${sid}/users/${uid}`)
        );
        character = parseHudCharacter(snap.data()?.character);
      } catch (e) {
        console.warn("fetchCharacterImagesByUserIds:", uid, e);
      }
      if (!character && lobbyCharByUid[uid]) {
        character = parseHudCharacter(lobbyCharByUid[uid]);
      }
      const src = characterImageSrc(character);
      if (src) result[uid] = src;
    })
  );
  return result;
}

/** Plain object safe for Firestore (no undefined). */
export function serializeGameStateForFirestore(state) {
  if (!state) return null;
  return JSON.parse(JSON.stringify(state));
}

export function gameStateSignature(state) {
  if (!state) return "";
  return JSON.stringify({
    board: state.board,
    currentPlayer: state.currentPlayer,
    phase: state.phase,
    selectedPiece: state.selectedPiece,
    piecesPlaced: state.piecesPlaced,
    moveHistoryLength: state.moveHistory?.length ?? 0,
    winner: state.winner,
    isDraw: state.isDraw,
    winningLine: state.winningLine,
  });
}

function lobbyGameDocRef(sessionId, lobbyName) {
  return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
}

function lobbyUserDocRef(sessionId, lobbyName, userId) {
  return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${userId}`);
}

function lobbyUsersCollectionRef(sessionId, lobbyName) {
  return collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/users`);
}

function authRoleFromSessionData(data) {
  const src = String(data?.source ?? data?.roles ?? data?.role ?? "")
    .trim()
    .toUpperCase();
  if (src === "GUEST_USER" || src === "GUEST" || src === "GUESTUSER") {
    return "GUEST_USER";
  }
  return "ORG_USER";
}

/** Live lobby players with X/O roles from Firebase character slots. */
export async function fetchLobbyLivePlayers(sessionId, lobbyName) {
  if (!sessionId || !lobbyName) return [];
  try {
    const snap = await getDocs(lobbyUsersCollectionRef(sessionId, lobbyName));
    const players = [];
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      if (String(data.status ?? "").trim().toLowerCase() !== "live") return;
      const role = characterToPlayerRole(data.character);
      if (!role) return;
      const userId = String(data.userId || d.id || "").trim();
      if (!userId) return;
      players.push({
        userId,
        name: String(data.name ?? "").trim(),
        email: String(data.email ?? "").trim(),
        emp_id: String(data.emp_id ?? data.employeeId ?? "").trim(),
        employeeId: String(data.employeeId ?? data.emp_id ?? "").trim(),
        role,
        lobbyCharacter: data.character,
      });
    });
    await Promise.all(
      players.map(async (p) => {
        try {
          const sessionUserSnap = await getDoc(
            doc(database, `sessions/${sessionId}/users/${p.userId}`)
          );
          const sessionData = sessionUserSnap.data() || {};
          const sessionToken = String(sessionData.token ?? "").trim();
          if (sessionToken) p.token = sessionToken;
          p.authRole = authRoleFromSessionData(sessionData);
          p.name = resolveLobbyPlayerDisplayName(p, sessionData);
        } catch (e) {
          console.warn("fetchLobbyLivePlayers: session token", p.userId, e);
        }
      })
    );
    return players;
  } catch (e) {
    console.error("fetchLobbyLivePlayers failed:", e);
    return [];
  }
}

/**
 * Ensure only one client submits combined lobby PVP scores per finished match.
 */
export async function isLobbyGameScoreSubmitted(sessionId, lobbyName, matchSig) {
  if (!sessionId || !lobbyName || !matchSig) return false;
  try {
    const snap = await getDoc(lobbyGameDocRef(sessionId, lobbyName));
    return String(snap.data()?.shiftGameScoreSig ?? "").trim() === matchSig;
  } catch {
    return false;
  }
}

export function isLobbyGameScoresReadyFromDoc(data, matchSig) {
  const sig = String(matchSig ?? "").trim();
  if (!sig || !data) return false;
  if (
    data.shiftGameScoresReady === true &&
    String(data.shiftGameLastMatchSig ?? "").trim() === sig
  ) {
    return true;
  }
  if (String(data.shiftGameLastMatchSig ?? "").trim() !== sig) return false;
  if (String(data.status ?? "").trim().toLowerCase() !== "gameover") return false;
  const stats = data.shiftGamePlayerStats;
  return Array.isArray(stats) && stats.length >= 1;
}

/**
 * True after APIs finished: stats persisted, match sig on last match, status gameover.
 * shiftGameScoreSig alone is set at claim time (before APIs) — do not use for navigation.
 */
export async function isLobbyGameScoresReady(sessionId, lobbyName, matchSig) {
  if (!sessionId || !lobbyName || !matchSig) return false;
  try {
    const snap = await getDoc(lobbyGameDocRef(sessionId, lobbyName));
    return isLobbyGameScoresReadyFromDoc(snap.data(), matchSig);
  } catch {
    return false;
  }
}

export async function waitForLobbyGameScoresReady(
  sessionId,
  lobbyName,
  matchSig,
  maxMs = 15000
) {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    if (await isLobbyGameScoresReady(sessionId, lobbyName, matchSig)) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return false;
}

export async function claimLobbyGameOverScores(sessionId, lobbyName, matchSig, userId) {
  if (!sessionId || !lobbyName || !matchSig) return false;
  const gameRef = lobbyGameDocRef(sessionId, lobbyName);
  try {
    return await runTransaction(database, async (tx) => {
      const snap = await tx.get(gameRef);
      if (!snap.exists()) return false;
      const existing = String(snap.data()?.shiftGameScoreSig ?? "").trim();
      if (existing === matchSig) return false;
      tx.set(
        gameRef,
        {
          shiftGameScoreSig: matchSig,
          shiftGameScoreSubmittedBy: String(userId ?? ""),
          shiftGameScoreSubmittedAt: Date.now(),
        },
        { merge: true }
      );
      return true;
    });
  } catch (e) {
    console.error("claimLobbyGameOverScores failed:", e);
    return false;
  }
}

/**
 * Live lobby PVP sync. Pass onRemoteUpdateRef — invoked on every Firestore snapshot
 * so the game board updates immediately (do not rely only on React state + useEffect).
 */
export function useLobbyPvpFirestore({
  enabled,
  sessionId,
  lobbyName,
  userId,
  onRemoteUpdateRef,
  onLobbyDocUpdateRef,
}) {
  const [resolvedRole, setResolvedRole] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const pushInFlightRef = useRef(false);
  const pendingPushStateRef = useRef(null);

  useEffect(() => {
    if (!enabled || !sessionId || !lobbyName || !userId) {
      setReady(false);
      return undefined;
    }

    let active = true;

    const bootstrap = async () => {
      try {
        const userSnap = await getDoc(lobbyUserDocRef(sessionId, lobbyName, userId));
        const role = characterToPlayerRole(userSnap.data()?.character);
        if (!active) return;
        if (!role) {
          setError("Missing player character. Rejoin the lobby.");
          setReady(false);
          return;
        }
        setResolvedRole(role);
        setReady(true);
        setError(null);
      } catch (e) {
        if (!active) return;
        console.error("Failed to load lobby player role:", e);
        setError("Could not load multiplayer role.");
        setReady(false);
      }
    };

    bootstrap();

    const gameRef = lobbyGameDocRef(sessionId, lobbyName);
    const unsubscribe = onSnapshot(
      gameRef,
      (snap) => {
        if (!active) return;
        if (!snap.exists()) return;

        const data = snap.data();

        if (typeof onLobbyDocUpdateRef?.current === "function") {
          onLobbyDocUpdateRef.current({
            status: String(data?.status ?? "").trim().toLowerCase(),
            statusUpdatedAt: Number(data?.statusUpdatedAt) || 0,
            shiftGameScoresReady: data?.shiftGameScoresReady === true,
            shiftGameLastMatchSig: String(data?.shiftGameLastMatchSig ?? "").trim(),
            shiftGamePlayerStats: data?.shiftGamePlayerStats,
          });
        }

        const raw = data?.shiftGameState ?? null;
        if (!raw || !Array.isArray(raw.board) || raw.board.length !== 9) return;

        const remote = serializeGameStateForFirestore(raw);
        const meta = {
          updatedBy: String(data?.shiftGameUpdatedBy ?? ""),
          revision: Number(data?.shiftGameRevision) || remote.moveHistory?.length || 0,
          updatedAt: Number(data?.shiftGameUpdatedAt) || 0,
          rematchSeq: Number(data?.shiftGameRematchSeq) || 0,
        };

        if (typeof onRemoteUpdateRef?.current === "function") {
          onRemoteUpdateRef.current(remote, meta);
        }
      },
      (e) => {
        if (!active) return;
        console.error("Lobby game state subscription failed:", e);
        setError("Lost connection to game room.");
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [enabled, sessionId, lobbyName, userId]);

  const ensureInitialRemoteState = useCallback(
    async (settings) => {
      if (!sessionId || !lobbyName) return false;
      const gameRef = lobbyGameDocRef(sessionId, lobbyName);
      const snap = await getDoc(gameRef);
      const existing = snap.data()?.shiftGameState;
      if (existing && Array.isArray(existing.board)) return true;

      const rematchSeq = Number(snap.data()?.shiftGameRematchSeq) || 0;
      const initial = createInitialState({
        maxPieces: settings?.maxPieces ?? 4,
        allowDiagonal: settings?.allowDiagonal !== false,
        startingPlayer: startingPlayerForRematchSeq(rematchSeq),
      });

      const payload = serializeGameStateForFirestore(initial);
      await setDoc(
        gameRef,
        {
          shiftGameState: payload,
          shiftGameRevision: 0,
          shiftGameUpdatedAt: Date.now(),
          shiftGameUpdatedBy: userId,
          shiftGameRematchSeq: 0,
        },
        { merge: true }
      );
      return true;
    },
    [sessionId, lobbyName, userId]
  );

  const pushGameState = useCallback(
    async (nextState) => {
      if (!sessionId || !lobbyName || !nextState) {
        return false;
      }

      if (pushInFlightRef.current) {
        pendingPushStateRef.current = nextState;
        return true;
      }

      pushInFlightRef.current = true;
      let stateToWrite = nextState;
      try {
        const gameRef = lobbyGameDocRef(sessionId, lobbyName);
        for (;;) {
          const payload = serializeGameStateForFirestore(stateToWrite);
          const revision = payload.moveHistory?.length ?? 0;
          await setDoc(
            gameRef,
            {
              shiftGameState: payload,
              shiftGameRevision: revision,
              shiftGameUpdatedAt: Date.now(),
              shiftGameUpdatedBy: userId,
            },
            { merge: true }
          );
          const pending = pendingPushStateRef.current;
          pendingPushStateRef.current = null;
          if (!pending) break;
          if (gameStateSignature(pending) === gameStateSignature(stateToWrite)) break;
          stateToWrite = pending;
        }
        return true;
      } catch (e) {
        console.error("Failed to push lobby game state:", e);
        setError("Failed to sync your move.");
        return false;
      } finally {
        pushInFlightRef.current = false;
        const leftover = pendingPushStateRef.current;
        if (leftover) {
          pendingPushStateRef.current = null;
          void pushGameState(leftover);
        }
      }
    },
    [sessionId, lobbyName, userId]
  );

  const resetRemoteGame = useCallback(
    async (settings) => {
      if (!sessionId || !lobbyName) return false;
      try {
        const gameRef = lobbyGameDocRef(sessionId, lobbyName);
        const snap = await getDoc(gameRef);
        const prevRematch = Number(snap.data()?.shiftGameRematchSeq) || 0;
        const nextRematch = prevRematch + 1;
        const startingPlayer = startingPlayerForRematchSeq(nextRematch);
        const initial = createInitialState({
          maxPieces: settings?.maxPieces ?? 4,
          allowDiagonal: settings?.allowDiagonal !== false,
          startingPlayer,
        });
        const payload = serializeGameStateForFirestore(initial);
        await setDoc(
          gameRef,
          {
            shiftGameState: payload,
            shiftGameRevision: 0,
            shiftGameUpdatedAt: Date.now(),
            shiftGameUpdatedBy: userId,
            shiftGameRematchSeq: nextRematch,
            shiftGameScoreSig: "",
          },
          { merge: true }
        );
        return true;
      } catch (e) {
        console.error("Failed to reset lobby game:", e);
        setError("Failed to start a new round.");
        return false;
      }
    },
    [sessionId, lobbyName, userId]
  );

  const pullLatestGameState = useCallback(async () => {
    if (!sessionId || !lobbyName) return false;
    try {
      const snap = await getDoc(lobbyGameDocRef(sessionId, lobbyName));
      if (!snap.exists()) return false;

      const data = snap.data();
      const raw = data?.shiftGameState ?? null;
      if (!raw || !Array.isArray(raw.board) || raw.board.length !== 9) return false;

      const remote = serializeGameStateForFirestore(raw);
      const meta = {
        updatedBy: String(data?.shiftGameUpdatedBy ?? ""),
        revision: Number(data?.shiftGameRevision) || remote.moveHistory?.length || 0,
        updatedAt: Number(data?.shiftGameUpdatedAt) || 0,
        rematchSeq: Number(data?.shiftGameRematchSeq) || 0,
      };

      if (typeof onRemoteUpdateRef?.current === "function") {
        onRemoteUpdateRef.current(remote, meta);
      }
      return true;
    } catch (e) {
      console.error("Failed to pull lobby game state:", e);
      return false;
    }
  }, [sessionId, lobbyName, onRemoteUpdateRef]);

  return {
    resolvedRole,
    ready,
    error,
    ensureInitialRemoteState,
    pushGameState,
    resetRemoteGame,
    pullLatestGameState,
  };
}

const DEFAULT_PVP_SETTINGS = { allowDiagonal: true, maxPieces: 4 };

/** Sync lobby room status so all clients can route (waiting / leaderboard / game). */
export async function setLobbyGameStatus(sessionId, lobbyName, status) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  const nextStatus = String(status ?? "").trim().toLowerCase();
  if (!sid || !lobby || !nextStatus) return false;
  try {
    await setDoc(
      lobbyGameDocRef(sid, lobby),
      { status: nextStatus, statusUpdatedAt: Date.now() },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("setLobbyGameStatus failed:", e);
    return false;
  }
}

/** Tell every client in the lobby to open the game (Play Again). */
export async function signalLobbyPlayAgain(sessionId, lobbyName) {
  return setLobbyGameStatus(sessionId, lobbyName, "play");
}

/** Unique userIds who clicked Play Again on the scoreboard (max 2 for PVP). */
export function readLobbyPlayAgainReadyUserIds(data) {
  const raw = data?.playAgainReadyUserIds;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    const uid = String(id ?? "").trim();
    if (uid && !seen.has(uid)) {
      seen.add(uid);
      ids.push(uid);
    }
  }
  return ids.slice(0, 2);
}

export function countLobbyPlayAgainReady(data) {
  return Math.min(readLobbyPlayAgainReadyUserIds(data).length, 2);
}

/**
 * Register this user as ready for Play Again. Returns readyCount (0–2) and bothReady.
 * Does not start the match — call restartLobbyPvpGameForPlayAgain when bothReady.
 */
export async function registerLobbyPlayAgainReady(sessionId, lobbyName, userId) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  const uid = String(userId ?? "").trim();
  if (!sid || !lobby || !uid) {
    return { ok: false, readyCount: 0, bothReady: false };
  }

  try {
    const gameRef = lobbyGameDocRef(sid, lobby);
    const result = await runTransaction(database, async (transaction) => {
      const snap = await transaction.get(gameRef);
      const data = snap.data() || {};
      const prev = Array.isArray(data.playAgainReadyUserIds)
        ? data.playAgainReadyUserIds.map((id) => String(id).trim()).filter(Boolean)
        : [];
      const ready = prev.includes(uid) ? prev : [...prev, uid];
      const readyCount = Math.min(ready.length, 2);
      const bothReady = ready.length >= 2;

      transaction.set(
        gameRef,
        {
          playAgainReadyUserIds: ready,
          playAgainReadyUpdatedAt: Date.now(),
        },
        { merge: true }
      );

      return { ok: true, readyCount, bothReady };
    });
    return result;
  } catch (e) {
    console.error("registerLobbyPlayAgainReady failed:", e);
    return { ok: false, readyCount: 0, bothReady: false };
  }
}

/** Tell every client the match ended (scoreboard). */
export async function signalLobbyGameOver(sessionId, lobbyName) {
  return setLobbyGameStatus(sessionId, lobbyName, "gameover");
}

/**
 * Mirror PVP cumulative score + time to Firestore (lobby doc + each user doc).
 * Uses updateScore API results when available; otherwise scoreUsers payload.
 */
export async function persistLobbyPvpScoresToFirestore({
  sessionId,
  lobbyName,
  scoreUsers,
  updateResult,
  matchSig,
  winner,
  isDraw,
  matchTimeSeconds,
}) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  if (!sid || !lobby) return false;

  const players = buildPlayerStatsForFirestore(scoreUsers, updateResult);
  if (!players.length) return false;

  const matchTime = toDurationString(matchTimeSeconds ?? 0);
  const gameRef = lobbyGameDocRef(sid, lobby);

  try {
    const now = Date.now();
    await setDoc(
      gameRef,
      {
        shiftGamePlayerStats: players,
        shiftGameLastMatchSig: String(matchSig ?? "").trim(),
        shiftGameLastMatchAt: now,
        shiftGameLastMatchTime: matchTime,
        shiftGameLastWinner: winner ? String(winner) : "",
        shiftGameLastIsDraw: Boolean(isDraw),
        shiftGameScoresReady: true,
        shiftGameScoresReadyAt: now,
        playAgainReadyUserIds: [],
        status: "gameover",
        statusUpdatedAt: now,
      },
      { merge: true }
    );

    await Promise.all(
      players.map((p) =>
        setDoc(
          lobbyUserDocRef(sid, lobby, p.userId),
          {
            points: p.points,
            score: p.points,
            time: p.time,
            totalTime: p.time,
            updatedAt: Date.now(),
          },
          { merge: true }
        )
      )
    );

    return true;
  } catch (e) {
    console.error("persistLobbyPvpScoresToFirestore failed:", e);
    return false;
  }
}

/** Reset lobby board after scoreboard (no React hooks). */
export async function resetLobbyPvpGameAfterScoreboard(
  settings = DEFAULT_PVP_SETTINGS
) {
  const { sessionId, lobbyName, userId } = readLobbyContextFromStorage();
  if (!sessionId || !lobbyName) return false;
  try {
    const gameRef = lobbyGameDocRef(sessionId, lobbyName);
    const snap = await getDoc(gameRef);
    const prevRematch = Number(snap.data()?.shiftGameRematchSeq) || 0;
    const nextRematch = prevRematch + 1;
    const startingPlayer = startingPlayerForRematchSeq(nextRematch);
    const initial = createInitialState({
      maxPieces: settings?.maxPieces ?? 4,
      allowDiagonal: settings?.allowDiagonal !== false,
      startingPlayer,
    });
    const payload = serializeGameStateForFirestore(initial);
    await setDoc(
      gameRef,
      {
        shiftGameState: payload,
        shiftGameRevision: 0,
        shiftGameUpdatedAt: Date.now(),
        shiftGameUpdatedBy: userId,
        shiftGameRematchSeq: nextRematch,
        shiftGameScoreSig: "",
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("resetLobbyPvpGameAfterScoreboard failed:", e);
    return false;
  }
}

/**
 * Play Again for PVP: reset the board AND signal 'play' in one atomic write.
 * This ensures both players see the fresh board when they navigate to the game.
 */
export async function restartLobbyPvpGameForPlayAgain(
  sessionId,
  lobbyName,
  settings = DEFAULT_PVP_SETTINGS
) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  const { userId } = readLobbyContextFromStorage();
  if (!sid || !lobby) return false;

  try {
    const gameRef = lobbyGameDocRef(sid, lobby);
    const snap = await getDoc(gameRef);
    const docData = snap.data() || {};
    const currentStatus = String(docData.status ?? "").trim().toLowerCase();
    if (currentStatus === "play") {
      return true;
    }

    const prevRematch = Number(docData.shiftGameRematchSeq) || 0;
    const nextRematch = prevRematch + 1;
    const startingPlayer = startingPlayerForRematchSeq(nextRematch);
    const initial = createInitialState({
      maxPieces: settings?.maxPieces ?? 4,
      allowDiagonal: settings?.allowDiagonal !== false,
      startingPlayer,
    });
    const payload = serializeGameStateForFirestore(initial);
    const now = Date.now();

    await setDoc(
      gameRef,
      {
        shiftGameState: payload,
        shiftGameRevision: 0,
        shiftGameUpdatedAt: now,
        shiftGameUpdatedBy: userId,
        shiftGameRematchSeq: nextRematch,
        shiftGameScoreSig: "",
        shiftGameScoresReady: false,
        playAgainReadyUserIds: [],
        status: "play",
        statusUpdatedAt: now,
        shiftGamePlayAgainAt: now,
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("restartLobbyPvpGameForPlayAgain failed:", e);
    return false;
  }
}

/**
 * Forfeit the current PVP game - makes the other player the winner.
 * Called when a player clicks Home/Back during an active game.
 */
export async function forfeitLobbyPvpGame(sessionId, lobbyName, forfeitingPlayerRole) {
  const sid = String(sessionId ?? "").trim();
  const lobby = String(lobbyName ?? "").trim();
  if (!sid || !lobby || !forfeitingPlayerRole) return false;

  try {
    const gameRef = lobbyGameDocRef(sid, lobby);
    const snap = await getDoc(gameRef);
    const currentState = snap.data()?.shiftGameState;
    
    if (!currentState || currentState.winner || currentState.isDraw) {
      return false;
    }

    const winnerRole = forfeitingPlayerRole === 'X' ? 'O' : 'X';
    const updatedState = {
      ...currentState,
      winner: winnerRole,
      forfeit: true,
      forfeitedBy: forfeitingPlayerRole,
    };

    const now = Date.now();
    await setDoc(
      gameRef,
      {
        shiftGameState: serializeGameStateForFirestore(updatedState),
        shiftGameUpdatedAt: now,
        shiftGameForfeitAt: now,
        shiftGameForfeitedBy: forfeitingPlayerRole,
        status: "gameover",
        statusUpdatedAt: now,
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("forfeitLobbyPvpGame failed:", e);
    return false;
  }
}

/** Join link + PIN from host Firestore user doc (presenter / hostInitialized). */
export async function fetchLobbyJoinMeta(sessionId, lobbyName) {
  const lobby = String(lobbyName ?? "").trim();
  if (!sessionId || !lobby) {
    return { lobby: "", link: "", loginPin: null };
  }

  try {
    const snap = await getDocs(lobbyUsersCollectionRef(sessionId, lobby));
    let link = "";
    let loginPin = null;

    snap.docs.forEach((d) => {
      const data = d.data() || {};
      if (!data.presenter && !data.hostInitialized) return;
      const docLink = String(data.link ?? "").trim();
      const rawPin = data.loginPin ?? data.login_pin ?? data.pin;
      if (docLink) link = docLink;
      if (rawPin != null && rawPin !== "") {
        const n = Number(rawPin);
        if (Number.isFinite(n)) loginPin = Math.trunc(n);
      }
    });

    if (loginPin == null) {
      const fromLobby = Number.parseInt(lobby, 10);
      if (Number.isFinite(fromLobby)) loginPin = fromLobby;
    }

    return { lobby, link, loginPin };
  } catch (e) {
    console.error("fetchLobbyJoinMeta failed:", e);
    return { lobby, link: "", loginPin: null };
  }
}

export function readLobbyContextFromStorage() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    const userData = JSON.parse(raw) || {};
    const sessionId = String(userData.sessionId ?? "").trim();
    const lobbyName =
      String(sessionStorage.getItem(CURRENT_LOBBY_KEY) ?? "").trim() ||
      String(userData.lobby ?? "").trim();
    const userId = String(
      userData.userId || userData.userid || userData.id || ""
    ).trim();
    const link = String(
      userData.link ?? userData.joinLink ?? sessionStorage.getItem("mythFactLobbyLink") ?? ""
    ).trim();
    const rawPin =
      userData.loginPin ?? userData.login_pin ?? userData.pin ?? null;
    let loginPin = null;
    if (rawPin != null && rawPin !== "") {
      const n = Number(rawPin);
      if (Number.isFinite(n)) loginPin = Math.trunc(n);
    }
    if (loginPin == null && lobbyName) {
      const fromLobby = Number.parseInt(lobbyName, 10);
      if (Number.isFinite(fromLobby)) loginPin = fromLobby;
    }
    return { sessionId, lobbyName, userId, userData, link, loginPin };
  } catch {
    return {
      sessionId: "",
      lobbyName: "",
      userId: "",
      userData: {},
      link: "",
      loginPin: null,
    };
  }
}
